import { cacheGet, cacheSet } from 'services/commonQuery';

/**
 * 세무관리 — 고객사(tx_client_company) 로컬 스토어
 * 키: erp.tax.clientCompany|{tenant}  — 세무법인(tenant) 단위로 원천 분리
 * mapper 연결 전 localStorage. 다른 세무법인 row 는 키 자체가 달라 조회되지 않는다.
 */

const clientCache = new Map();

export const CLIENT_STORAGE_PREFIX = 'erp.tax.clientCompany';

export const YN_OPTIONS = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
];

export const PROXY_INPUT_OPTIONS = [
  { value: 'N', label: '고객사입력' },
  { value: 'Y', label: '대신입력' },
];

export function createClientUid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `TC-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function clientStorageKey(tenantCode) {
  return `${CLIENT_STORAGE_PREFIX}|${tenantCode || ''}`;
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

export function emptyClient(partial = {}) {
  return {
    clientUid: '',
    clientCode: '',
    clientName: '',
    bizNo: '',
    corpNo: '',
    ceoName: '',
    bizType: '',
    bizItem: '',
    tel: '',
    email: '',
    address: '',
    taxOffice: '',
    clientGroupPath: '',
    proxyInputYn: 'N',
    useYn: 'Y',
    sortOrder: 10,
    remark: '',
    mod: '',
    ...partial,
  };
}

export function digitsOnly(v) {
  return String(v || '').replace(/\D/g, '');
}

export function formatBizNo(v) {
  const d = digitsOnly(v);
  if (d.length !== 10) return String(v || '').trim();
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

export function nextSortOrderBy10(rows) {
  let max = 0;
  for (const r of rows || []) {
    const n = Number(r?.sortOrder);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return Math.ceil((max + 1) / 10) * 10;
}

export function nextClientCode(rows) {
  let max = 0;
  for (const r of rows || []) {
    const m = String(r?.clientCode || '').match(/^C(\d+)$/i);
    if (m) max = Math.max(max, Number(m[1]));
  }
  return `C${String(max + 1).padStart(3, '0')}`;
}

function stripRow(row) {
  const { mod, _tempId, ...rest } = row || {};
  return { ...rest, mod: '' };
}

export function getCachedClients(tenantCode) {
  return cacheGet(clientCache, tenantCode) || [];
}

export function loadClients(tenantCode) {
  const tc = tenantCode || '';
  const mapped = (readJson(clientStorageKey(tc), []) || []).map((r) => ({ ...r, mod: '' }));
  cacheSet(clientCache, tc, mapped);
  return mapped;
}

export function filterClients(rows, values = {}) {
  const keyword = String(values.keyword || '').trim().toLowerCase();
  const useYn = String(values.useYn || '').trim();
  const proxyInputYn = String(values.proxyInputYn || '').trim();
  return (rows || []).filter((r) => {
    if (r?.mod === 'D') return false;
    if (useYn && String(r.useYn || '') !== useYn) return false;
    if (proxyInputYn && String(r.proxyInputYn || 'N') !== proxyInputYn) return false;
    if (!keyword) return true;
    const hay = [
      r.clientCode,
      r.clientName,
      r.bizNo,
      r.ceoName,
      r.clientGroupPath,
      r.taxOffice,
    ]
      .map((x) => String(x || '').toLowerCase())
      .join(' ');
    return hay.includes(keyword);
  });
}

export function clientOptions(tenantCode, { includeAll = false } = {}) {
  const list = (getCachedClients(tenantCode).length
    ? getCachedClients(tenantCode)
    : loadClients(tenantCode)
  ).filter((r) => r.useYn !== 'N' && r.mod !== 'D');
  const opts = list.map((r) => ({
    value: r.clientCode,
    label: `${r.clientCode} ${r.clientName || ''}`.trim(),
  }));
  if (includeAll) return [{ value: '', label: '선택' }, ...opts];
  return opts;
}

export function findClient(tenantCode, clientCode) {
  return (loadClients(tenantCode) || []).find(
    (r) => r.clientCode === clientCode && r.mod !== 'D'
  ) || null;
}

export function validateClients(rows) {
  const list = (rows || []).filter((r) => r && r.mod !== 'D');
  const codes = new Set();
  const errors = [];
  for (const r of list) {
    const code = String(r.clientCode || '').trim();
    const name = String(r.clientName || '').trim();
    if (!code) errors.push('고객사코드는 필수입니다.');
    if (!name) errors.push(`고객사명 누락 (${code || '신규'})`);
    if (code) {
      const key = code.toUpperCase();
      if (codes.has(key)) errors.push(`고객사코드 중복: ${code}`);
      codes.add(key);
    }
    const biz = digitsOnly(r.bizNo);
    if (biz && biz.length !== 10) {
      errors.push(`사업자번호 10자리 아님: ${code || name}`);
    }
  }
  return errors;
}

/**
 * 그리드 저장 — 현재 필터 밖 행은 유지하고, 필터 안 행만 그리드 내용으로 치환
 */
export function persistClients(tenantCode, gridRows, filterValues = {}) {
  const tc = tenantCode || '';
  const prev = loadClients(tc);
  const inFilter = new Set(filterClients(prev, filterValues).map((r) => r.clientUid).filter(Boolean));
  const outside = prev.filter((r) => r.clientUid && !inFilter.has(r.clientUid));
  const active = (gridRows || [])
    .filter((r) => r && r.mod !== 'D')
    .map((r) => {
      const next = stripRow({
        ...emptyClient(),
        ...r,
        clientUid: r.clientUid || createClientUid(),
        clientCode: String(r.clientCode || '').trim(),
        clientName: String(r.clientName || '').trim(),
        bizNo: formatBizNo(r.bizNo),
        proxyInputYn: r.proxyInputYn === 'Y' ? 'Y' : 'N',
        useYn: r.useYn === 'N' ? 'N' : 'Y',
      });
      return next;
    });
  const next = [...outside, ...active];
  writeJson(clientStorageKey(tc), next);
  cacheSet(clientCache, tc, next);
  return filterClients(next, filterValues);
}
